/* eslint-disable max-len */
import { ChangeDetectorRef, Component, ElementRef, ViewChild, signal, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Sort, SortDirection } from '@angular/material/sort';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { Observable, noop, distinctUntilChanged, filter, switchMap, take, tap } from 'rxjs';

import { Store } from '@ngxs/store';
import orderBy from 'lodash-es/orderBy';
import { LngLatBounds } from 'mapbox-gl';
import { TsMenuItem } from 'tsui/lib/ts-menu/ts-menu.component';
import { UserState } from '@state/user/state';
import { FieldDef, QueryConfig } from '@core/models';
import GraphqlService from '@services/graphql.service';
import { NewPermission } from '@core/enums/permission';
import { FileFormat, RelatedToType } from '@shared/enums';
import { GraphqlConnection, GraphqlFilter } from '@shared/models/graphql';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { generateDownloadableFile, getAllowedFields } from '@shared/functions';
import { ConfirmService } from '@shared/components/widgets/dialog/confirm/confirm.service';
import { Compset, Project, Property } from 'app/modules/projects/projects.types';
import { SelectableFieldDef } from '@shared/components/column-selector/column-selector.types';
import { ProjectInsightsService } from 'app/modules/projects/details/insights/insights.service';
import { AccountState } from '@state/account/state';

@Component({
  selector: 'project-insights-comps',
  templateUrl: './comps.component.html',
  styleUrls: ['./comps.component.scss'],
})
export class ProjectInsightsCompsComponent {
  @ViewChild('compsEdit', { static: false }) compsEditDiv: ElementRef;

  private allComps: Compset[] = [];
  private fieldDefs: FieldDef[] = [];
  private fieldDefOptions: any[] = [];
  private compsetDefinition: any;
  private queryFilters: Partial<QueryConfig.QueryFilters> = {};
  private defaultDealDefinition = JSON.stringify({
    comps: [],
    fields: ['dist_miles'],
  });
  private defaultPropertyDefinition = JSON.stringify({
    comps: [],
    fields: ['index', 'selected', 'name', 'address', 'dist_miles', 'remove'],
  });

  property: Property;
  displayColumns: string[];
  editing = signal(true);
  isLoading = signal(false);
  editTitle = signal(false);
  averages: any = [];
  compsetFieldDefs: FieldDef[] = [];
  selectedOrder: string[] = [];
  compset: Compset;
  title: string;
  pageEvent: PageEvent;
  selectedItemsDatasource = new MatTableDataSource<Compset>();
  bounds: LngLatBounds;
  layers = [];
  selectedMapStyle = 'mapbox://styles/mapbox/streets-v11';
  data: Data;
  sortBy: string = 'dist_miles';
  sortDirection: SortDirection = 'asc';
  project: Project;
  compsMenuOptions: TsMenuItem[] = [
    {
      label: 'Edit',
      icon: 'ts_pen',
      disabled: () => !this.canEditDeal(),
      action: () => {
        this.openEditComp();
      },
    },
    {
      label: 'Export as CSV',
      icon: 'file_copy',
      disabled: () => (this.isCompsetStatic || !this.editing()) && !this.allComps?.length,
      action: () => {
        this.exportProjects(FileFormat.CSV);
      },
    },
    {
      label: 'Export as PDF',
      icon: 'picture_as_pdf',
      disabled: () => (this.isCompsetDynamic || this.editing()) && !this.allComps?.length,
      action: () => {
        this.exportProjects(FileFormat.PDF);
      },
    },
  ];

  private readonly destroyRef = inject(DestroyRef);

  private get accountId(): number {
    return +this.store.selectSnapshot(AccountState.getAccountId);
  }

  get isCompsetDynamic(): boolean {
    return this.compset.compset_type === 'dynamic';
  }

  get isCompsetStatic(): boolean {
    return this.compset.compset_type === 'static';
  }

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _projectInsightsService: ProjectInsightsService,
    private _projectService: ProjectsService,
    private _propertiesService: PropertiesService,
    private readonly confirmService: ConfirmService,
    private readonly graphqlService: GraphqlService,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this._activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: Data) => {
      this.fieldDefOptions = data.options;
      this.data = data;
      this.project = this.data.project;
      this.editing.set(false);
      this.editTitle.set(false);
      this._changeDetectorRef.markForCheck();
    });

    this._projectInsightsService.compset$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        switchMap((compset) => {
          this.compset = compset;
          this.title = this.compset.title;
          this.compsetDefinition = JSON.parse(this.compset.definition || '{}');
          this.sortBy = this.compsetDefinition?.sortBy ?? 'dist_miles';
          this.sortDirection = this.compsetDefinition?.sortDirection ?? 'asc';
          this.setFieldDefs(this.compset.object_type);
          this.loadCompsetDefinition();

          return this._projectInsightsService.property$.pipe(distinctUntilChanged());
        }),
      )
      .subscribe((property) => {
        if (!property) {
          this.handlePropertyNotFound();
        } else {
          this.property = property;
          this.loadCompsetData();
          this._changeDetectorRef.markForCheck();
        }
      });
  }

  announceSortChange(sortState: Sort): void {
    this.sortBy = sortState.active;
    this.sortDirection = sortState.direction;
    this.compsetDefinition.sortBy = this.sortBy;
    this.compsetDefinition.sortDirection = this.sortDirection;

    this.updateCompset();
    this.loadCompsetData();
  }

  setFieldDefs(type: RelatedToType): void {
    this.fieldDefs = getAllowedFields(this.data.fieldDefs, type).sort((a, b) => a.label.localeCompare(b.label));
  }

  private loadCompsetDefinition(): void {
    if (this.compset) {
      if (this.compset.definition === null && this.compset.object_type === RelatedToType.Project) {
        this.compsetDefinition = JSON.parse(this.defaultDealDefinition);
      } else if (this.compset.definition === null && this.compset.object_type === RelatedToType.Property) {
        this.compsetDefinition = JSON.parse(this.defaultPropertyDefinition);
      } else {
        this.compsetDefinition = JSON.parse(this.compset.definition);
      }
      const fields = this.compsetDefinition?.fields || [];

      this.displayColumns = fields.filter((column: string) => this.lookupField(column));
      this.queryFilters = this.compsetDefinition?.filters ?? {};

      this.refreshSelectedFields(
        this.displayColumns.map((field: string) => this.lookupField(field)) as SelectableFieldDef[],
      );
    }
  }

  private loadCompsetData(): void {
    this.cleanseDisplayColumns();
    this.pageEvent = new PageEvent();
    this.pageEvent.pageIndex = 0;
    this.pageEvent.pageSize = 10;
    this.getComps(this.compsetDefinition.comps.map((id: number) => +id));
  }

  private getComps(ids: number[]): void {
    let result: Observable<any>;
    let resultKey: string;

    this.isLoading.set(true);

    const compRelatedToType = this.compset.object_type;
    const fields = this.requestDisplayColumns().concat(['latitude', 'longitude']);
    const filters = this.isCompsetStatic
      ? { fjson: JSON.stringify([['id', 'in', ids.length > 0 ? ids : this.project.id]]) }
      : {
          ...this.queryFilters,
          location: {
            near: {
              lat: parseFloat(this.property.address.latitude.toString()),
              lon: parseFloat(this.property.address.longitude.toString()),
            },
            within: `${this.compsetDefinition?.miles?.toString() || '10'}mi`,
          },
        };

    if (compRelatedToType === RelatedToType.Project) {
      result = this._projectService.exploreProjects(fields, filters, 0, 25, this.sortBy, this.sortDirection);
      resultKey = 'projects';
    } else if (compRelatedToType === RelatedToType.Property) {
      result = this._propertiesService.getProperties(
        fields,
        {
          fjson: JSON.stringify([['id', 'in', ids.length > 0 ? ids : this.project.id]]),
        },
        0,
        ids.length,
      );

      resultKey = 'properties';
    }

    result?.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((results: any) => {
      this.allComps = results[resultKey].filter((comp) => +comp.id !== +this.project.id);
      const sortedComps = orderBy(this.allComps, [this.sortBy], [this.sortDirection.toString() as 'asc' | 'desc']);

      let index = results.pagination.startIndex + 1;
      for (const comp of sortedComps) {
        comp.index = index;
        index++;
      }

      this.bounds = this.calculateBounds(sortedComps);

      if (this.bounds.isEmpty()) {
        this.bounds = null;
      }

      this.selectedItemsDatasource = new MatTableDataSource();
      this.selectedItemsDatasource.data = sortedComps;

      this.calculateAverages();
      this.isLoading.set(false);
      this._changeDetectorRef.markForCheck();
    });
  }

  private calculateBounds(comps: Compset[]): LngLatBounds {
    return comps.reduce((bounds, comp) => {
      if (comp.longitude && comp.latitude) {
        return bounds.extend([comp.longitude, comp.latitude]);
      }
      return bounds;
    }, new LngLatBounds());
  }

  requestDisplayColumns(): string[] {
    let columns = this.displayColumns;

    columns = columns.filter((item, index) => {
      return columns.indexOf(item) == index;
    });

    return this.filterColumns(columns);
  }

  getTableColumns(): string[] {
    return this.filterColumns(this.displayColumns);
  }

  private filterColumns(columns: string[]): string[] {
    return columns.filter((item) => !['selected', 'name', 'address', 'index', 'remove'].includes(item));
  }

  scrollToEditTable(): void {
    setTimeout(() => {
      this.compsEditDiv.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }, 0);
  }

  openEditTitle(): void {
    this.editTitle.set(true);
  }

  openEditComp(): void {
    this.editing.set(true);
    this.discardTitle();
  }

  discardTitle(): void {
    this.editTitle.set(false);
    this.title = this.compset.title;
  }

  saveTitle(): void {
    this.compset.title = this.title;
    this.updateCompset();
    this.editTitle.set(false);
  }

  deleteCompset(): void {
    this.confirmService
      .openConfirmationModal({
        message: 'Are you sure that you wish to delete this Compset?',
      })
      .pipe(
        filter((res: boolean) => res),
        switchMap(() => this._projectInsightsService.deleteCompset(this.compset.id)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this._router.navigate(['../../market'], { relativeTo: this._activatedRoute });
        this._changeDetectorRef.markForCheck();
      });
  }

  removeComp(comp: Compset): void {
    this.confirmService
      .openConfirmationModal({
        message: 'Are you sure that you wish to remove this property?',
      })
      .pipe(
        filter((res: boolean) => res),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.compsetDefinition.comps = this.compsetDefinition.comps.filter((id) => +id !== +comp.id);
        this.saveCompset(this.compsetDefinition.comps);
      });
  }

  onSave(event: null | { compset: Compset; data: any[] }): void {
    // User cancelled the edit.
    if (event === null) {
      this.editing.set(false);
      this.getComps(this.compsetDefinition.comps.map((id: number) => +id));
      return;
    } else {
      const { compset, data } = event;
      this.compset = compset;
      this.title = this.compset.title;

      if (this.isCompsetDynamic && data) {
        this.selectedItemsDatasource.data = [...data];
        this._changeDetectorRef.detectChanges();
      }

      this._projectInsightsService
        .saveCompset(this.compset)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.compsetDefinition = JSON.parse(this.compset.definition);
          this.sortBy = this.compsetDefinition?.sortBy ?? 'dist_miles';
          this.sortDirection = this.compsetDefinition?.sortDirection ?? 'asc';
          this.getComps(this.compsetDefinition.comps.map((id: number) => +id));
          this.editing.set(false);
          this._changeDetectorRef.detectChanges();
        });
    }
  }

  onDataSourceChange(newDataSource: MatTableDataSource<any>): void {
    this.selectedItemsDatasource = newDataSource;
    this._changeDetectorRef.detectChanges();
  }

  onSaveColumns(selectedFieldDefs: SelectableFieldDef[]): void {
    this.displayColumns = selectedFieldDefs.map((fieldDef) => fieldDef.name);
    this.saveCompset(this.compsetDefinition.comps.map((id: number) => +id));
    this.refreshSelectedFields(selectedFieldDefs);
  }

  private refreshSelectedFields(selectedFieldDefs: SelectableFieldDef[]): void {
    this.compsetFieldDefs = [
      ...this.fieldDefs.map((compsetFieldDef: FieldDef) => {
        const selected = !!selectedFieldDefs.find(
          (selectedField: SelectableFieldDef) => selectedField.name === compsetFieldDef.name,
        );

        return {
          ...compsetFieldDef,
          selected,
        };
      }),
    ];

    this.cleanseDisplayColumns();

    this.selectedOrder = selectedFieldDefs.map(({ name }) => name);
  }

  private updateCompset(): void {
    const { ...filters } = this.queryFilters;

    this.compset.definition = JSON.stringify({
      comps: this.compsetDefinition.comps,
      sortBy: this.compsetDefinition?.sortBy ?? 'dist_miles',
      sortDirection: this.compsetDefinition?.sortDirection ?? 'asc',
      fields: this.displayColumns,
      filters: filters,
    });
    this._projectInsightsService
      .saveCompset(this.compset)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._changeDetectorRef.detectChanges();
      });
  }

  private saveCompset(ids: number[]): void {
    this.updateCompset();
    this.getComps(ids);
  }

  private calculateAverages(): void {
    let sums = {};
    const comps = this.isCompsetStatic ? this.selectedItemsDatasource.data : this.allComps;

    for (let comp of comps) {
      for (let fieldName of this.displayColumns) {
        let fieldDef = this.lookupField(fieldName);

        if (fieldDef) {
          if (
            fieldDef.type == 'money' ||
            fieldDef.type == 'number' ||
            fieldDef.type == 'decimal' ||
            fieldDef.type == 'percent'
          ) {
            let val = comp[fieldName];
            if (val) {
              if (!sums[fieldName]) {
                sums[fieldName] = [];
              }
              val = parseFloat(val);
              if (val > 0) {
                sums[fieldName].push(val);
              }
            }
          }
        }
      }
    }

    this.averages = [];

    for (let sumColumn in sums) {
      this.averages.push({
        key: sumColumn,
        value: sums[sumColumn].reduce((a, b) => a + b, 0) / sums[sumColumn].length,
      });
    }
  }

  lookupField(name: string): FieldDef {
    return this.fieldDefs.find((fieldDef: FieldDef) => fieldDef.name == name);
  }

  getOptionName(fieldDef: FieldDef, value): string {
    if (value === null) return '';
    if (fieldDef.meta.option_name) {
      if (this.fieldDefOptions[fieldDef.meta.option_name])
        return this.fieldDefOptions[fieldDef.meta.option_name].find((option) => option.id == value)?.name;
    } else if (fieldDef.meta.options) {
      return fieldDef.meta.options.find((option) => option == value);
    }
    return '';
  }

  private cleanseDisplayColumns(): void {
    this.displayColumns = this.displayColumns.filter(
      (column) => !['name', 'address', 'selected', 'index', 'remove'].includes(column),
    );
    this.displayColumns.unshift('address');
    if (this.compset.object_type === RelatedToType.Property) this.displayColumns.unshift('name');
    this.displayColumns.unshift('selected');
    this.displayColumns.unshift('index');
    this.displayColumns.push('remove');
  }

  trackByFn(index: number, item: any): any {
    return item.id;
  }

  isFieldTypeAllowedForPopup(fieldDef: FieldDef): boolean {
    return !['number', 'decimal', 'percent', 'money', 'date', 'datetime'].includes(fieldDef?.type);
  }

  isFieldTypeAllowed(fieldDef: FieldDef): boolean {
    return !['number', 'decimal', 'percent', 'money', 'select', 'date', 'datetime'].includes(fieldDef?.type);
  }

  private exportProjects(format: FileFormat): void {
    const fields = ['address_string', ...this.requestDisplayColumns()];
    const fileName = `${this.property.address.address_string}-Comps`;

    if (!fields.find((field) => field === 'id')) {
      fields.unshift('id');
    }

    let filters = (
      this.isCompsetStatic
        ? { fjson: JSON.stringify([['id', 'in', this.selectedItemsDatasource.data.map((item) => +item.id)]]) }
        : {
            ...this.queryFilters,
            location: {
              near: {
                lat: parseFloat(this.property.address.latitude.toString()),
                lon: parseFloat(this.property.address.longitude.toString()),
              },
              within: `${this.compsetDefinition?.miles?.toString() || '10'}mi`,
            },
          }
    ) as GraphqlFilter;
    this.graphqlService
      .query(
        {
          connection: GraphqlConnection.projects,
          fields,
          filter: {
            ...filters,
            fjson: filters.fjson || JSON.stringify([]),
            queryObject: { group: '' },
            pagination: {
              page: 1,
              size: 1000,
            },
            sort: [
              {
                field: this.sortBy,
                order: this.sortDirection,
              },
            ],
          },
        },
        {
          fileName,
          format,
          excludeFields: this.requestDisplayColumns().find((field) => field === 'id') ? [] : ['id'],
        },
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((result) => {
          this._changeDetectorRef.detectChanges();
          generateDownloadableFile(result as any, format, fileName);
        }),
      )
      .subscribe(noop);
  }

  private handlePropertyNotFound(): void {
    const firstPropertyId = this._projectInsightsService.properties?.[0]?.id;
    const fallbackRoute = ['../../../'];

    if (firstPropertyId) {
      this._router.navigate([...fallbackRoute, firstPropertyId, 'comps', this.compset.id], {
        relativeTo: this._activatedRoute,
      });
    } else {
      this._router.navigate(fallbackRoute, { relativeTo: this._activatedRoute });
    }
  }

  canEditDeal(): boolean {
    return (
      this.store.selectSnapshot(UserState.hasPermission(NewPermission.DEALS_EDIT)) ||
      this.project?.permissions?.is_editable
    );
  }
}
